import { Link } from "react-router-dom";
import BrandQR from "../components/BrandQR";

const LAST_UPDATED = "May 1, 2026";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      {/* Nav */}
      <nav className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
            </span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-heading text-4xl font-semibold tracking-tight mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-zinc-500 mb-10">Last Updated: {LAST_UPDATED}</p>

        <div className="prose prose-zinc max-w-none space-y-8 text-zinc-700 leading-relaxed">

          <p>
            Welcome to <strong>Growithevent</strong> ("we," "our," "us"). We are committed to
            protecting your privacy and ensuring transparency in how your data is handled.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and safeguard your
            information when you use our website:{" "}
            <a
              href="https://www.growithevent.com"
              className="text-zinc-900 underline underline-offset-2"
            >
              www.growithevent.com
            </a>
          </p>
          <p>
            By using our platform, you agree to the terms outlined in this policy.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              1. Information We Collect
            </h2>

            <h3 className="font-semibold text-zinc-900 mb-2">
              1.1 Personal Information (Participants)
            </h3>
            <p className="mb-2">When you register for an event, we may collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Full Name</li>
              <li>Sponsor Name</li>
              <li>Identification details (optional)</li>
              <li>Phone Number</li>
              <li>Email Address</li>
            </ul>

            <h3 className="font-semibold text-zinc-900 mt-6 mb-2">
              1.2 Organizer Information
            </h3>
            <p className="mb-2">If you create or manage events, we may collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name</li>
              <li>Email Address</li>
              <li>
                Payment and bank details (processed securely via third-party payment
                providers)
              </li>
            </ul>

            <h3 className="font-semibold text-zinc-900 mt-6 mb-2">
              1.3 Technical Information
            </h3>
            <p className="mb-2">
              When you use our website, we may automatically collect:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>IP address</li>
              <li>Device and browser information</li>
              <li>Basic usage data</li>
            </ul>
            <p className="mt-3">
              We may use cookies or similar technologies to improve user experience.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              2. How We Use Your Information
            </h2>
            <p className="mb-2">We use the collected data for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>To facilitate event registrations</li>
              <li>To process payments securely</li>
              <li>To generate QR codes for event entry and attendance tracking</li>
              <li>To communicate with users regarding registrations or updates</li>
              <li>To improve and optimize our platform</li>
              <li>To ensure platform security and prevent misuse</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              3. Payment Processing
            </h2>
            <p className="mb-2">
              Payments on our platform are processed through{" "}
              <strong>Cashfree</strong>.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We do not store your full payment or banking details.</li>
              <li>
                Payment data is handled securely by the payment gateway in accordance
                with their privacy policies.
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              4. Data Sharing and Disclosure
            </h2>
            <p className="mb-4">
              We may share your information in the following cases:
            </p>

            <h3 className="font-semibold text-zinc-900 mb-2">
              4.1 With Event Organizers
            </h3>
            <p>
              Participant data (such as name, contact details, and attendance status)
              is shared with the respective event organizer.
            </p>

            <h3 className="font-semibold text-zinc-900 mt-6 mb-2">
              4.2 With Service Providers
            </h3>
            <p className="mb-2">
              We may share data with trusted third-party providers, including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Hosting services (e.g., Vercel)</li>
              <li>Database providers (e.g., MongoDB Atlas)</li>
              <li>Payment processors (e.g., Cashfree)</li>
            </ul>

            <h3 className="font-semibold text-zinc-900 mt-6 mb-2">
              4.3 Legal Requirements
            </h3>
            <p>
              We may disclose information if required by law or to protect our rights.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              5. Data Retention
            </h2>
            <p className="mb-2">We retain your data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>As long as necessary to provide our services</li>
              <li>Until you request deletion of your data</li>
              <li>Or as required by applicable laws</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              6. User Rights
            </h2>
            <p className="mb-2">
              You have the following rights regarding your data:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your data</li>
              <li>Opt out of communications</li>
              <li>Update or modify your information</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at:{" "}
              <a
                href="mailto:growithevent@gmail.com"
                className="text-zinc-900 underline underline-offset-2"
              >
                growithevent@gmail.com
              </a>
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              7. Data Security
            </h2>
            <p className="mb-2">
              We take reasonable measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Secure HTTPS connections</li>
              <li>Controlled access to systems</li>
              <li>Secure database infrastructure</li>
            </ul>
            <p className="mt-3">
              However, no system is completely secure, and we cannot guarantee
              absolute security.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              8. International Data Transfers
            </h2>
            <p>
              Your data is primarily stored on servers located in <strong>India</strong>.
              If any data is processed outside India, we ensure appropriate safeguards
              are in place in compliance with applicable data protection laws.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              9. AI and Automated Processing
            </h2>
            <p className="mb-2">Currently:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We do not use your data to train AI models</li>
              <li>We do not perform automated decision-making affecting users</li>
            </ul>
            <p className="mt-3">
              If this changes, we will update this Privacy Policy accordingly.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              10. Cookies and Tracking
            </h2>
            <p className="mb-2">
              We may use basic cookies or similar technologies to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Improve website functionality</li>
              <li>Enhance user experience</li>
            </ul>
            <p className="mt-3">
              You can control cookies through your browser settings.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              11. Third-Party Links
            </h2>
            <p>
              Our platform may contain links to third-party services. We are not
              responsible for their privacy practices and encourage you to review their
              respective privacy policies.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              12. Children's Privacy
            </h2>
            <p>
              Our platform is not intended for children under the age of{" "}
              <strong>18</strong>. We do not knowingly collect data from minors. If
              you believe a minor has provided us with personal information, please
              contact us immediately so we can delete it.
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              13. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this policy from time to time. Changes will be posted on
              this page with an updated date. We encourage you to review this page
              periodically to stay informed.
            </p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              14. Contact Us
            </h2>
            <p className="mb-3">
              If you have any questions or concerns regarding this Privacy Policy,
              please contact us:
            </p>
            <ul className="space-y-1">
              <li>
                <span className="text-zinc-500">Email:</span>{" "}
                <a
                  href="mailto:growithevent@gmail.com"
                  className="text-zinc-900 underline underline-offset-2"
                >
                  growithevent@gmail.com
                </a>
              </li>
              <li>
                <span className="text-zinc-500">Address:</span> Gurugram, Haryana,
                India
              </li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="max-w-4xl mx-auto px-6 py-10 text-sm text-zinc-500 border-t border-zinc-200 flex justify-between">
        <span>© Growithevent · MVP build</span>
        <Link to="/" className="hover:text-zinc-900 transition-colors">
          Back to Home
        </Link>
      </footer>
    </div>
  );
}
